<template>
  <div class="max-w-6xl mx-auto">
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="p-6 border-b border-background-lighter">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-2xl font-bold text-text-primary">Payment History</h2>
            <p class="text-text-secondary mt-1">View all your payment transactions and deposits</p>
          </div>

          <!-- Filters -->
          <div class="mt-4 sm:mt-0 flex space-x-3">
            <select
                v-model="filters.status"
                @change="fetchPaymentHistory"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
            >
              <option value="">All Status</option>
              <option value="1">Approved</option>
              <option value="0">Pending</option>
              <option value="-1">Rejected</option>
            </select>

            <input
                v-model="filters.date_from"
                type="date"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
                @change="fetchPaymentHistory"
            />

            <input
                v-model="filters.date_to"
                type="date"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
                @change="fetchPaymentHistory"
            />
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>

        <!-- Payment History Table -->
        <div v-else-if="paymentHistory.length > 0" class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-background-lighter">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Date</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Description</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Amount</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Currency</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Comment</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Status</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Bonus</th>
            </tr>
            </thead>
            <tbody class="divide-y divide-background-lighter">
            <tr
                v-for="payment in paymentHistory"
                :key="payment.id"
                :class="[
                  'hover:bg-background-lighter',
                  getRowClass(payment.status)
                ]"
            >
              <td class="px-4 py-3 text-sm text-text-primary">
                {{ formatDate(payment.created_at) }}
              </td>


              <td>

              <span v-if="!payment.reçu || payment.reçu === '/' || payment.reçu === null">
                N/A
              </span>
                            <a
                    v-else
                    :href="getImageUrl(payment.reçu)"
                    target="_blank"
                >
                  <img
                      :src="getImageUrl(payment.reçu)"
                      style="height: 50px; width: 50px"
                      alt="Receipt image"
                  />
                </a>
              </td>

              <td class="px-4 py-3 text-sm font-medium text-text-primary">
                {{ formatAmount(payment.montant, payment.currency) }}
              </td>

              <td class="px-4 py-3 text-sm text-text-primary">
                <span class="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full font-medium">
                  {{ payment.currency.toUpperCase() }}
                </span>
              </td>

              <td class="px-4 py-3 text-sm text-text-secondary max-w-xs truncate">
                {{ payment.link || 'No comment' }}
              </td>

              <td class="px-4 py-3">
                <span :class="getStatusClass(payment.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                  {{ getStatusText(payment.status) }}
                </span>
              </td>

              <td class="px-4 py-3 text-sm text-text-primary">
                <span v-if="payment.bonus > 0" class="text-green-600 font-medium">
                  +{{ payment.bonus }}
                </span>
                <span v-else class="text-gray-400">
                  -
                </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-8">
          <svg class="mx-auto h-12 w-12 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
          </svg>
          <p class="mt-2 text-text-secondary">No payment history found</p>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.total > pagination.per_page" class="mt-6 flex justify-center">
          <nav class="flex items-center space-x-2">
            <button
                @click="changePage(pagination.current_page - 1)"
                :disabled="pagination.current_page === 1"
                class="px-3 py-2 text-sm bg-background border border-background-lighter rounded hover:bg-background-lighter disabled:opacity-50"
            >
              Previous
            </button>

            <span class="px-3 py-2 text-sm text-text-secondary">
              Page {{ pagination.current_page }} of {{ pagination.last_page }}
            </span>

            <button
                @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.last_page"
                class="px-3 py-2 text-sm bg-background border border-background-lighter rounded hover:bg-background-lighter disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {ref, onMounted} from 'vue';
import {getUserPaymentHistory} from '@/services/api.js';
import {useDataStore} from '@/stores/dataStore.js';
import Swal from 'sweetalert2';

export default {
  name: 'PaymentHistory',
  setup() {
    const dataStore = useDataStore();

    const paymentHistory = ref([]);
    const isLoading = ref(true);
    const imageBaseUrl = import.meta.env.VITE_IMAGE_URL;

    const getImageUrl = (item) => {
      if (!item) return '/placeholder-image.png';
      return item.image_url || `${imageBaseUrl}/storage/${item}`;
    };
    const filters = ref({
      status: '',
      date_from: '',
      date_to: ''
    });

    const pagination = ref({
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0
    });

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatAmount = (amount, currency) => {
      if (!amount) return '0.00';
      const formattedAmount = parseFloat(amount).toFixed(2);
      return `${currency === 'dzd' ? '' : currency}${formattedAmount}${currency === 'dzd' ? ' DZD' : ''}`;
    };

    const getStatusClass = (status) => {
      const classes = {
        1: 'bg-green-100 text-green-800',     // Approved
        0: 'bg-yellow-100 text-yellow-800',   // Pending
        '-1': 'bg-red-100 text-red-800'       // Rejected
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusText = (status) => {
      const texts = {
        1: 'Approved',
        0: 'Pending',
        '-1': 'Rejected'
      };
      return texts[status] || 'Unknown';
    };

    const getRowClass = (status) => {
      if (status === -1) {
        return 'bg-red-20';
      } else if (status === 0) {
        return 'bg-yellow-20';
      }
      return '';
    };

    const fetchPaymentHistory = async (page = 1) => {
      isLoading.value = true;

      try {
        const params = {
          page,
          per_page: pagination.value.per_page,
          status: filters.value.status,
          date_from: filters.value.date_from,
          date_to: filters.value.date_to
        };

        const response = await getUserPaymentHistory(params);

        // Adjust based on your actual response structure
        const data = response.data || response;
        paymentHistory.value = data.paymentHistory || data || [];
        pagination.value = data.pagination || pagination.value;

      } catch (error) {
        console.error('Failed to fetch payment history:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load payment history. Please try again.',
          icon: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };

    const changePage = (page) => {
      if (page >= 1 && page <= pagination.value.last_page) {
        fetchPaymentHistory(page);
      }
    };

    onMounted(() => {
      fetchPaymentHistory();
    });

    return {
      paymentHistory,
      isLoading,
      filters,
      pagination,
      formatDate,
      formatAmount,
      getStatusClass,
      getStatusText,
      getRowClass,
      fetchPaymentHistory,
      changePage,
      getImageUrl
    };
  }
}
</script>

<style scoped>
.truncate {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
